import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { customFetch } from '../apiClient';

const VendorPaymentForm = () => {
  const initialState = {
    payDate: new Date().toISOString().split('T')[0], vendorID: "", amount: "", payMode: "CASH",
    chqNo: "", chqDate: "", remarks: "", billNo: "", billDate: ""
  };

  const [form, setForm] = useState({ ...initialState });
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  
  const [payments, setPayments] = useState([]);
  const [vendorList, setVendorList] = useState([]);

  // ✅ NAYA STATE: Vendor ka due store karne ke liye
  const [currentDue, setCurrentDue] = useState(null);
  const [loadingDue, setLoadingDue] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [dateFilter, setDateFilter] = useState({ fromDate: firstDayOfMonth, toDate: today });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [editForm, setEditForm] = useState({ ...initialState });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const formRef = useRef(null);
  const editFormRef = useRef(null);
  const navigate = useNavigate();

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) { const timer = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(timer); }
  }, [success]);
  useEffect(() => {
    if (error) { const timer = setTimeout(() => setError(""), 4000); return () => clearTimeout(timer); }
  }, [error]);
  useEffect(() => {
    if (editError) { const timer = setTimeout(() => setEditError(""), 4000); return () => clearTimeout(timer); }
  }, [editError]);

  // Initial Load - Set Focus
  useEffect(() => {
    loadVendors();
    loadPayments();
    
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.querySelector('select:not([disabled]), input:not([disabled]):not([type="hidden"])')?.focus();
      }
    }, 200);
  }, []); 

  // Edit Modal Open hone par Focus set karna
  useEffect(() => {
    if (showEditModal) {
      setTimeout(() => {
        if (editFormRef.current) {
          editFormRef.current.querySelector('select:not([disabled]), input:not([disabled]):not([type="hidden"])')?.focus();
        }
      }, 200);
    }
  }, [showEditModal]);

  // ✅ NAYA LOGIC: Jab bhi Vendor change ho, uska Due fetch karo
  useEffect(() => {
    if (form.vendorID) {
      fetchVendorDue(form.vendorID);
    } else {
      setCurrentDue(null);
    }
  }, [form.vendorID]);
// ✅ NAYA LOGIC: Single vendor ka due fetch karne ke liye (Fast performance)
  const fetchVendorDue = async (vid) => {
    setLoadingDue(true);
    try {
      const res = await customFetch(`/MediVendorDue/due/${vid}`);
      if (res.ok) {
        const result = await res.json();
        setCurrentDue(result.totalDue);
      }
    } catch (err) {
      console.error("Error fetching due", err);
    } finally {
      setLoadingDue(false);
    }
  };

  const loadPayments = async () => {
    setIsSearching(true);
    try {
      const res = await customFetch(`/VendorPayment?fromDate=${dateFilter.fromDate}&toDate=${dateFilter.toDate}`);
      if (res.ok) setPayments(await res.json());
    } catch (err) { console.error(err); } finally { setIsSearching(false); }
  };

  const loadVendors = async () => {
    try {
      const res = await customFetch('/VendorDT');
      if (res.ok) setVendorList(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      let updated = { ...prev, [name]: value };
      if (name === "payMode") {
        if (value === "CASH") { updated.chqNo = ""; updated.chqDate = ""; }
        else if (value !== "CHEQUE") { updated.chqDate = ""; }
      }
      return updated;
    });
  };

  const handleFilterChange = (e) => setDateFilter(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const form = e.currentTarget; 
    const focusableElements = form.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    
    if (!focusableElements || focusableElements.length === 0) return;
    
    const elementsArray = Array.from(focusableElements);
    const currentIndex = elementsArray.indexOf(e.target);

    if (e.target.tagName.toLowerCase() === 'button') return;

    e.preventDefault();
    const nextIndex = currentIndex + 1;
    if (nextIndex < elementsArray.length) {
      elementsArray[nextIndex].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendorID) { setError("Please select a vendor."); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setError("Enter a valid amount."); return; }

    setLoading(true); setError(""); setSuccess("");
    try {
      const formData = new FormData();
      formData.append("PayDate", form.payDate);
      formData.append("VendorID", form.vendorID);
      formData.append("Amount", form.amount);
      formData.append("PayMode", form.payMode);
      formData.append("ChqNo", form.chqNo?.trim() || "");
      formData.append("ChqDate", form.chqDate?.trim() || "");
      formData.append("Remarks", form.remarks?.trim() || "");
      formData.append("BillNo", form.billNo?.trim() || "");
      formData.append("BillDate", form.billDate?.trim() || "");

      const res = await customFetch('/VendorPayment', { method: "POST", body: formData });
      if (!res.ok) throw new Error(await res.text());
      
      setSuccess("Payment added successfully!");
      setForm({ ...initialState });
      setCurrentDue(null); // Reset Due
      loadPayments();
      
      setTimeout(() => formRef.current?.querySelector('select')?.focus(), 100);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const openEditModal = (payment) => {
    setEditPayment(payment);
    setEditForm({
      payDate: payment.payDate ? payment.payDate.split('T')[0] : "", 
      vendorID: payment.vendorID || "", 
      amount: payment.amount || "", 
      payMode: payment.payMode || "CASH",
      chqNo: payment.chqNo || "", 
      chqDate: payment.chqDate || "", 
      remarks: payment.remarks || "", 
      billNo: payment.billNo || "", 
      billDate: payment.billDate || ""
    });
    setEditError(""); setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false); setEditPayment(null); setEditError("");
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => {
      let updated = { ...prev, [name]: value };
      if (name === "payMode") {
        if (value === "CASH") { updated.chqNo = ""; updated.chqDate = ""; }
        else if (value !== "CHEQUE") { updated.chqDate = ""; }
      }
      return updated;
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.vendorID) { setEditError("Please select a vendor."); return; }
    setEditLoading(true);

    try {
      const formData = new FormData();
      formData.append("PayDate", editForm.payDate);
      formData.append("VendorID", editForm.vendorID);
      formData.append("Amount", editForm.amount);
      formData.append("PayMode", editForm.payMode);
      formData.append("ChqNo", editForm.chqNo?.trim() || "");
      formData.append("ChqDate", editForm.chqDate?.trim() || "");
      formData.append("Remarks", editForm.remarks?.trim() || "");
      formData.append("BillNo", editForm.billNo?.trim() || "");
      formData.append("BillDate", editForm.billDate?.trim() || "");

      const res = await customFetch(`/VendorPayment/${editPayment.payID}`, { method: "PUT", body: formData });
      if (!res.ok) throw new Error(await res.text());
      
      loadPayments(); setSuccess("Updated Successfully"); closeEditModal();
    } catch (err) { setEditError(err.message); } finally { setEditLoading(false); }
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment record?")) return;
    try {
      const res = await customFetch(`/VendorPayment/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
      
      setPayments(prev => prev.filter(p => p.payID !== id)); 
      setSuccess("Deleted Successfully");
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="vendor-container">
      <div className="vendor-card">
        <div className="vendor-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-cash-coin"></i></div>
            <div className="header-text"><h5>Vendor Payments</h5><p>Manage payouts to vendors</p></div>
          </div>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger"><i className="bi bi-exclamation-circle-fill"></i> {error}</div>}
          {success && <div className="alert-custom alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          <div className="form-section">
            <div className="section-title"><i className="bi bi-plus-circle"></i><span>Add Payment</span></div>
            <div className="section-content">
                <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
                    <div className="form-grid">
                        
                        {/* ✅ YAHAN DUE BALANCE DIKHAYA GAYA HAI */}
                        <div className="form-group span-2">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label className="form-label m-0">Vendor <span className="required">*</span></label>
                                {form.vendorID && (
                                    <span style={{ 
                                        fontSize: '11px', 
                                        fontWeight: '700', 
                                        color: currentDue > 0 ? '#e53e3e' : '#38a169',
                                        background: currentDue > 0 ? '#fff5f5' : '#f0fff4',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        border: `1px solid ${currentDue > 0 ? '#feb2b2' : '#9ae6b4'}`
                                    }}>
                                        {loadingDue ? (
                                            "Fetching Due..."
                                        ) : (
                                            `Due: ₹${currentDue?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}`
                                        )}
                                    </span>
                                )}
                            </div>
                            <select name="vendorID" value={form.vendorID} onChange={handleChange} className="form-input" required disabled={loading}>
                                <option value="">Select Vendor</option>
                                {vendorList.map(v => <option key={v.vid} value={v.vid}>{v.vendorName}</option>)}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label className="form-label">Pay Date</label>
                            <input type="date" name="payDate" value={form.payDate} onChange={handleChange} className="form-input" disabled={loading}/>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Amount (₹) <span className="required">*</span></label>
                            <input type="number" step="0.01" name="amount" value={form.amount} onChange={handleChange} className="form-input" required disabled={loading} placeholder="0.00"/>
                        </div>
                        
                        <div className="form-group">
                            <label className="form-label">Pay Mode</label>
                            <select name="payMode" value={form.payMode} onChange={handleChange} className="form-input" disabled={loading}>
                                <option value="CASH">CASH</option>
                                <option value="CHEQUE">CHEQUE</option>
                                <option value="NEFT">NEFT</option>
                                <option value="RTGS">RTGS</option>
                                <option value="UPI">UPI</option>
                            </select>
                        </div>

                        {form.payMode !== 'CASH' && (
                          <div className="form-group">
                              <label className="form-label">{form.payMode === 'CHEQUE' ? 'Cheque No' : 'Transaction No'}</label>
                              <input name="chqNo" value={form.chqNo} onChange={handleChange} className="form-input" disabled={loading} placeholder={form.payMode === 'CHEQUE' ? 'Cheque No' : 'Txn No'}/>
                          </div>
                        )}
                        {form.payMode === 'CHEQUE' && (
                          <div className="form-group">
                              <label className="form-label">Cheque Date</label>
                              <input type="date" name="chqDate" value={form.chqDate} onChange={handleChange} className="form-input" disabled={loading}/>
                          </div>
                        )}

                        <div className="form-group span-1">
                            <label className="form-label">Bill No</label>
                            <input name="billNo" value={form.billNo} onChange={handleChange} className="form-input" disabled={loading} placeholder="Bill No"/>
                        </div>
                        <div className="form-group span-4">
                            <label className="form-label">Remarks</label>
                            <input name="remarks" value={form.remarks} onChange={handleChange} className="form-input" disabled={loading} placeholder="Any remarks..."/>
                        </div>
                    </div>
                    <div className="form-footer">
                        <button type="submit" className="btn-submit" disabled={loading}>
                           {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-plus-lg"></i>} 
                           {loading ? " Saving..." : " Add Payment"}
                        </button>
                    </div>
                </form>
            </div>
          </div>

          {/* PAYMENT TABLE WITH SEARCH */}
          <div className="list-section">
             <div className="section-title d-flex-between">
                <div><i className="bi bi-list-ul"></i><span>Recent Payments ({payments.length})</span></div>
             </div>
             
             <div className="section-content p-0">
                <div className="filter-row">
                    <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                       <label className="form-label m-0">From:</label>
                       <input type="date" name="fromDate" value={dateFilter.fromDate} onChange={handleFilterChange} className="form-input filter-input" onKeyDown={(e) => e.key === 'Enter' && loadPayments()} />
                    </div>
                    <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                       <label className="form-label m-0">To:</label>
                       <input type="date" name="toDate" value={dateFilter.toDate} onChange={handleFilterChange} className="form-input filter-input" onKeyDown={(e) => e.key === 'Enter' && loadPayments()} />
                    </div>
                    
                    <div className="form-group">
                        <button type="button" className="btn-search" onClick={loadPayments} disabled={isSearching}>
                            {isSearching ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-search"></i>} Search
                        </button>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="custom-table">
                        <thead>
                           <tr>
                              <th className="col-sr">Sr</th>
                              <th>Date</th>
                              <th>Vendor Name</th>
                              <th>Mode</th>
                              <th>Ref No</th>
                              <th className="text-right">Amount (₹)</th>
                              <th className="text-center col-actions">Actions</th>
                           </tr>
                        </thead>
                        <tbody>
                            {payments.length === 0 ? <tr><td colSpan="7" className="empty-state">{isSearching ? "Searching..." : "No payment records found for selected dates."}</td></tr> : payments.map((p, i) => (
                                <tr key={p.payID}>
                                    <td className="col-sr text-muted fw-600">{i + 1}</td>
                                    <td>{p.payDate ? new Date(p.payDate).toLocaleDateString('en-IN') : '-'}</td>
                                    <td className="fw-500">{p.vendorName}</td>
                                    <td><span className={`badge ${p.payMode === 'CASH' ? 'bg-success' : 'bg-primary'}`}>{p.payMode}</span></td>
                                    <td>{p.chqNo || '-'}</td>
                                    <td className="text-right fw-600 text-danger">{p.amount?.toFixed(2) || '0.00'}</td>
                                    <td className="text-center col-actions">
                                        <div className="btn-group-actions">
                                            <button type="button" className="btn-icon btn-edit" onClick={() => openEditModal(p)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                                            <button type="button" className="btn-icon btn-delete" onClick={() => deletePayment(p.payID)} title="Delete"><i className="bi bi-trash"></i></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && (
        <>
          <div className="modal-overlay" onClick={closeEditModal}></div>
          <div className="modal-container">
            <div className="modal-card">
              <div className="modal-header">
                <div className="modal-title"><i className="bi bi-pencil-square"></i><span>Edit Payment</span></div>
                <button type="button" className="btn-modal-close" onClick={closeEditModal}><i className="bi bi-x-lg"></i></button>
              </div>
              <form ref={editFormRef} onSubmit={saveEdit} onKeyDown={handleFormKeyDown}>
                <div className="modal-body">
                  {editError && <div className="alert-custom alert-danger mb-2"><i className="bi bi-exclamation-circle-fill"></i> {editError}</div>}
                  <div className="form-grid">
                      <div className="form-group span-2">
                         <label className="form-label">Vendor <span className="required">*</span></label>
                         <select name="vendorID" value={editForm.vendorID} onChange={handleEditChange} className="form-input" required disabled={editLoading}>
                             <option value="">Select Vendor</option>
                             {vendorList.map(v => <option key={v.vid} value={v.vid}>{v.vendorName}</option>)}
                         </select>
                      </div>
                      <div className="form-group span-2">
                         <label className="form-label">Pay Date</label>
                         <input type="date" name="payDate" value={editForm.payDate} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                      <div className="form-group span-2">
                         <label className="form-label">Amount (₹)</label>
                         <input type="number" step="0.01" name="amount" value={editForm.amount} onChange={handleEditChange} className="form-input" required disabled={editLoading}/>
                      </div>
                      <div className="form-group span-2">
                         <label className="form-label">Pay Mode</label>
                         <select name="payMode" value={editForm.payMode} onChange={handleEditChange} className="form-input" disabled={editLoading}>
                             <option value="CASH">CASH</option>
                             <option value="CHEQUE">CHEQUE</option>
                             <option value="NEFT">NEFT</option>
                             <option value="RTGS">RTGS</option>
                             <option value="UPI">UPI</option>
                         </select>
                      </div>
                      {editForm.payMode !== 'CASH' && (
                        <div className="form-group span-2">
                           <label className="form-label">{editForm.payMode === 'CHEQUE' ? 'Cheque No' : 'Transaction No'}</label>
                           <input name="chqNo" value={editForm.chqNo} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                        </div>
                      )}
                      {editForm.payMode === 'CHEQUE' && (
                        <div className="form-group span-2">
                           <label className="form-label">Cheque Date</label>
                           <input type="date" name="chqDate" value={editForm.chqDate} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                        </div>
                      )}
                      <div className="form-group span-2">
                         <label className="form-label">Bill No</label>
                         <input name="billNo" value={editForm.billNo} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                      <div className="form-group span-4">
                         <label className="form-label">Remarks</label>
                         <input name="remarks" value={editForm.remarks} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-modal-cancel" onClick={closeEditModal} disabled={editLoading}>Cancel</button>
                  <button type="submit" className="btn-modal-submit" disabled={editLoading}>{editLoading ? "Updating..." : "Update Payment"}</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .vendor-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
        .vendor-card { background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; overflow: hidden; }
        .vendor-header { background: var(--theme-color, #0088cc); padding: 8px 12px; border-bottom: 2px solid #00d4aa; }
        .header-left { display: flex; align-items: center; gap: 8px; color: white; }
        .header-icon { width: 28px; height: 28px; background: rgba(255,255,255,0.2); border-radius: 4px; display: flex; justify-content: center; align-items: center; font-size: 14px; }
        .header-text h5 { margin: 0; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; font-size: 10px; color: rgba(255,255,255,0.85); }
        .card-content { padding: 12px; }
        .alert-custom { margin-bottom: 10px; padding: 6px 10px; border-radius: 4px; font-size: 11px; font-weight: 500; display: flex; align-items: center; gap: 6px; }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        
        .form-section, .list-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; margin-bottom: 12px; }
        .section-title { background: var(--theme-color, #3182ce); padding: 6px 10px; color: white; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .d-flex-between { display: flex; justify-content: space-between; align-items: center; }
        .section-content { padding: 10px; background: white; }
        .p-0 { padding: 0 !important; }
        
        .filter-row { display: flex; gap: 15px; padding: 10px 10px 10px 10px; background: white; border-bottom: 1px solid #edf2f7; align-items: center;}
        .filter-input { width: 120px; height: 28px; }
        .btn-search { padding: 0 12px; border: 1px solid #cbd5e0; background: #edf2f7; color: #2d3748; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; height: 28px; display: inline-flex; align-items: center; gap: 6px; }
        .btn-search:hover:not(:disabled) { background: #e2e8f0; }
        .m-0 { margin: 0; }

        .form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; align-items: end;}
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .span-2 { grid-column: span 2; }
        .span-4 { grid-column: span 4; }
        .form-label { font-size: 11px; font-weight: 600; color: #4a5568; display: flex; align-items: center; gap: 4px; }
        .required { color: #e53e3e; }
        .form-input { width: 100%; padding: 6px 8px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 12px; outline: none; transition: 0.2s; background: white; height: 30px; }
        .form-input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }
        .form-footer { margin-top: 10px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: right; }
        .btn-submit { padding: 0 16px; border: none; background: var(--theme-color, #3182ce); color: white; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; height: 30px; display: inline-flex; align-items: center; gap: 6px; }
        .btn-submit:hover:not(:disabled) { opacity: 0.9; }
        .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; text-align: left; }
        .custom-table th { background: #edf2f7; color: #4a5568; padding: 8px 10px; font-weight: 600; border-bottom: 1px solid #cbd5e0; white-space: nowrap; text-transform: uppercase; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; color: #2d3748; vertical-align: middle; }
        .custom-table tr:hover { background: #f8fafc; }
        .col-sr { width: 40px; text-align: center; }
        .col-actions { width: 80px; }
        .fw-500 { font-weight: 500; }
        .fw-600 { font-weight: 600; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .text-muted { color: #64748b; }
        .text-danger { color: #e53e3e; }
        .badge { display: inline-block; padding: 3px 6px; font-size: 10px; font-weight: 600; border-radius: 4px; color: white; }
        .bg-success { background: #38a169; }
        .bg-primary { background: #3182ce; }
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
        .btn-icon { width: 24px; height: 24px; border: none; border-radius: 3px; display: inline-flex; justify-content: center; align-items: center; cursor: pointer; font-size: 12px; transition: 0.2s; }
        .btn-edit { background: #ebf8ff; color: #3182ce; }
        .btn-edit:hover { background: #bee3f8; }
        .btn-delete { background: #fff5f5; color: #e53e3e; }
        .btn-delete:hover { background: #fed7d7; }
        .empty-state { text-align: center; padding: 20px; color: #94a3b8; font-style: italic; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1040; animation: fadeIn 0.2s; }
        .modal-container { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1050; width: 90%; max-width: 500px; animation: slideUp 0.2s; }
        .modal-card { background: white; border-radius: 6px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.15); }
        .modal-header { background: var(--theme-color, #3182ce); padding: 8px 12px; color: white; display: flex; justify-content: space-between; align-items: center; }
        .modal-title { font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .btn-modal-close { background: none; border: none; color: white; cursor: pointer; font-size: 14px; opacity: 0.8; display: flex; align-items: center; justify-content: center;}
        .btn-modal-close:hover { opacity: 1; }
        .modal-body { padding: 12px; }
        .modal-footer { padding: 8px 12px; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 6px; }
        .btn-modal-cancel { padding: 6px 12px; border: none; background: #edf2f7; color: #4a5568; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; }
        .btn-modal-cancel:hover { background: #e2e8f0; }
        .btn-modal-submit { padding: 6px 12px; border: none; background: var(--theme-color, #3182ce); color: white; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px;}
        .btn-modal-submit:hover:not(:disabled) { opacity: 0.9; }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translate(-50%, -40%); } to { opacity: 1; transform: translate(-50%, -50%); } }

        @media (max-width: 768px) {
          .form-grid { grid-template-columns: 1fr 1fr; }
          .span-4 { grid-column: span 2; }
        }
        @media (max-width: 480px) {
          .form-grid { grid-template-columns: 1fr; }
          .span-2, .span-4 { grid-column: span 1; }
          .btn-submit { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default VendorPaymentForm;